// Ported from advisor-3.0/src/SessionTimeout.js
// Replaces react-bootstrap Modal with MUI Dialog
import { Button, CircularProgress, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getTokenRemainingTime, refreshToken } from 'src/utility/auth';

const WARNING_THRESHOLD_SEC = 5 * 60;

const formatTime = (seconds: number) => {
  const mm = Math.max(0, Math.floor(seconds / 60));
  const ss = Math.max(0, seconds % 60);
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};

export default function SessionTimeout() {
  const [loading, setLoading] = useState(false);
  const [modalActive, setModalActive] = useState(false);
  const [remaining, setRemaining] = useState(0);

  const modalActiveRef = useRef(modalActive);
  useEffect(() => {
    modalActiveRef.current = modalActive;
  }, [modalActive]);

  const notifyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearNotifyTimer = () => {
    if (notifyTimerRef.current) {
      clearTimeout(notifyTimerRef.current);
      notifyTimerRef.current = null;
    }
  };

  const clearCountdown = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const timeOut = useCallback(() => {
    window.location.href = '/ns';
  }, []);

  const logOut = useCallback(() => {
    window.location.href = '/ns?logout=true';
  }, []);

  const checkSession = useCallback(() => {
    const remainingTime = getTokenRemainingTime();
    if (remainingTime === 0) {
      timeOut();
      return;
    }
    if (remainingTime < WARNING_THRESHOLD_SEC) {
      setRemaining(remainingTime);
      setModalActive(true);
    } else {
      setModalActive(false);
      clearNotifyTimer();
      const notifyTimeMs = (remainingTime - WARNING_THRESHOLD_SEC) * 1000;
      notifyTimerRef.current = setTimeout(() => {
        setRemaining(getTokenRemainingTime());
        setModalActive(true);
      }, notifyTimeMs);
    }
  }, [timeOut]);

  const extendTimeout = useCallback(async () => {
    if (getTokenRemainingTime() === 0) {
      logOut();
      return;
    }
    setLoading(true);
    try {
      await refreshToken();
      setModalActive(false);
      checkSession();
    } catch (err) {
      console.error('Token refresh failed', err);
      setModalActive(false);
    } finally {
      setLoading(false);
    }
  }, [checkSession, logOut]);

  // Modal countdown — auto logout when timer hits 0
  useEffect(() => {
    if (!modalActive) {
      clearCountdown();
      return;
    }
    countdownIntervalRef.current = setInterval(() => {
      const next = getTokenRemainingTime();
      setRemaining(next);
      if (next <= 0) {
        clearCountdown();
        logOut();
      }
    }, 1000);
    return clearCountdown;
  }, [modalActive, logOut]);

  // Activity handlers — refresh on click/keydown when modal not active; recheck on focus
  useEffect(() => {
    const onActivity = () => {
      if (!modalActiveRef.current) {
        void extendTimeout();
      }
    };
    const onFocus = () => checkSession();

    window.addEventListener('click', onActivity);
    window.addEventListener('keydown', onActivity);
    window.addEventListener('focus', onFocus);

    void extendTimeout();

    return () => {
      window.removeEventListener('click', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('focus', onFocus);
      clearNotifyTimer();
      clearCountdown();
    };
     
  }, []);

  return (
    <Dialog open={modalActive} disableEscapeKeyDown>
      <DialogContent>
        <Stack alignItems="center" spacing={2} p={2}>
          <Typography textAlign="center">
            Your session is about to expire. If you do not extend your session within 5 minutes,
            any unsaved progress will be lost.
          </Typography>
          <Typography variant="h5">{formatTime(remaining)}</Typography>
          {loading ? (
            <CircularProgress />
          ) : (
            <Stack direction="row" spacing={2}>
              <Button variant="contained" color="primary" onClick={() => void extendTimeout()}>
                Extend
              </Button>
              <Button variant="contained" color="primary" onClick={logOut}>
                Log Out
              </Button>
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
